"use client";

import { c as _c } from "react/compiler-runtime";
import * as PopoverPrimitives from "@radix-ui/react-popover";
import { useId, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { Button } from "@/components/ui";
import { cx } from "@/lib/tailwind-utils";
import type { UseEmailRecipientsReturn } from "./hooks/use-email-recipients";
import { RecipientSuggestion, useRecipientSuggestions } from "./hooks/use-recipient-suggestions";
interface EmailRecipientFieldProps {
  label: string;
  field: UseEmailRecipientsReturn;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /**
   * Drop the rounded box + focus-within border so the row reads as a flat
   * field instead of a boxed input. Used across the compose sheet and the
   * reply/forward composers so recipient rows sit flush with the composer chrome.
   */
  flat?: boolean;
}

/** Recipient input suggesting address-book contacts and patients. */
export function EmailRecipientField(props) {
  const $ = _c(3);
  const {
    suggestions
  } = useRecipientSuggestions(props.field.inputValue, props.field.recipients);
  let t0;
  if ($[0] !== props || $[1] !== suggestions) {
    t0 = <RecipientField {...props} suggestions={suggestions} />;
    $[0] = props;
    $[1] = suggestions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/** Pill-style recipient input; the caller decides what to suggest. */
export function RecipientField(t0) {
  const $ = _c(88);
  const {
    label,
    field,
    suggestions,
    placeholder: t1,
    autoFocus,
    className,
    flat
  } = t0;
  const placeholder = t1 === undefined ? "Add recipients..." : t1;
  const inputRef = useRef(null);
  const inputId = useId();
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const showSuggestions = open && suggestions.length > 0;
  const activeIndex = suggestions.length ? Math.min(highlightedIndex, suggestions.length - 1) : -1;
  let T0;
  let T1;
  let handleBlur;
  let handleChange;
  let handleFocus;
  let handleKeyDown;
  let selectSuggestion;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeIndex || $[1] !== className || $[2] !== field || $[3] !== flat || $[4] !== inputId || $[5] !== label || $[6] !== showSuggestions || $[7] !== suggestions) {
    const handleEdit = email => {
      field.edit(email);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) {
          return;
        }
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      });
    };
    let t10;
    if ($[23] !== field) {
      t10 = suggestion => {
        field.add(suggestion.email);
        setOpen(false);
        setHighlightedIndex(0);
        requestAnimationFrame(() => inputRef.current?.focus());
      };
      $[23] = field;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    selectSuggestion = t10;
    let t11;
    if ($[25] !== activeIndex || $[26] !== field || $[27] !== selectSuggestion || $[28] !== showSuggestions || $[29] !== suggestions) {
      t11 = e => {
        if (showSuggestions) {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex(i => (i + 1) % suggestions.length);
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex(i_0 => i_0 <= 0 ? suggestions.length - 1 : i_0 - 1);
            return;
          }
          if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
            return;
          }
          if ((e.key === "Enter" || e.key === "Tab") && activeIndex >= 0) {
            e.preventDefault();
            selectSuggestion(suggestions[activeIndex]);
            return;
          }
        }
        field.handleInputKeyDown(e);
      };
      $[25] = activeIndex;
      $[26] = field;
      $[27] = selectSuggestion;
      $[28] = showSuggestions;
      $[29] = suggestions;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    handleKeyDown = t11;
    let t12;
    if ($[31] !== field) {
      t12 = e_0 => {
        field.setInputValue(e_0.target.value);
        setHighlightedIndex(0);
        setOpen(true);
      };
      $[31] = field;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    handleChange = t12;
    let t13;
    if ($[33] !== field.inputValue) {
      t13 = () => {
        if (field.inputValue.trim().length >= 2) {
          setOpen(true);
        }
      };
      $[33] = field.inputValue;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    handleFocus = t13;
    let t14;
    if ($[35] !== field) {
      t14 = () => {
        setOpen(false);
        field.handleInputBlur();
      };
      $[35] = field;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    handleBlur = t14;
    T1 = PopoverPrimitives.Root;
    t8 = showSuggestions;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = next => {
        if (!next) {
          setOpen(false);
        }
      };
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    if ($[38] !== className) {
      t7 = cx("space-y-1", className);
      $[38] = className;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    T0 = PopoverPrimitives.Anchor;
    t6 = true;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = e_1 => {
        if (e_1.target !== e_1.currentTarget) {
          return;
        }
        e_1.preventDefault();
        inputRef.current?.focus();
      };
      $[40] = t2;
    } else {
      t2 = $[40];
    }
    const t15 = !flat && "rounded-md border border-transparent focus-within:border-border";
    if ($[41] !== t15) {
      t3 = cx("flex w-full cursor-text flex-wrap items-center gap-1.5 px-0 py-1.5 text-body transition-colors", t15);
      $[41] = t15;
      $[42] = t3;
    } else {
      t3 = $[42];
    }
    if ($[43] !== inputId || $[44] !== label) {
      t4 = <label htmlFor={inputId} className="shrink-0 cursor-text text-foreground-muted">{label}:</label>;
      $[43] = inputId;
      $[44] = label;
      $[45] = t4;
    } else {
      t4 = $[45];
    }
    t5 = field.recipients.map(email_0 => <span key={email_0} onDoubleClick={() => handleEdit(email_0)} title={`${email_0} — double-click to edit`} className="inline-flex max-w-full cursor-default select-none items-center gap-1 rounded-full border border-border bg-background-secondary py-0.5 pl-2.5 pr-1 text-caption text-foreground"><span className="min-w-0 truncate">{email_0}</span><Button type="button" size="icon-sm" variant="ghost" icon={<FiX className="size-3" />} onClick={() => field.remove(email_0)} aria-label={`Remove ${email_0}`} className="size-5 shrink-0 rounded-full text-foreground-muted hover:text-foreground" /></span>);
    $[0] = activeIndex;
    $[1] = className;
    $[2] = field;
    $[3] = flat;
    $[4] = inputId;
    $[5] = label;
    $[6] = showSuggestions;
    $[7] = suggestions;
    $[8] = T0;
    $[9] = T1;
    $[10] = handleBlur;
    $[11] = handleChange;
    $[12] = handleFocus;
    $[13] = handleKeyDown;
    $[14] = selectSuggestion;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    T0 = $[8];
    T1 = $[9];
    handleBlur = $[10];
    handleChange = $[11];
    handleFocus = $[12];
    handleKeyDown = $[13];
    selectSuggestion = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  const t10 = field.recipients.length === 0 ? placeholder : "";
  let t11;
  if ($[46] !== autoFocus || $[47] !== field.inputValue || $[48] !== handleBlur || $[49] !== handleChange || $[50] !== handleFocus || $[51] !== handleKeyDown || $[52] !== inputId || $[53] !== showSuggestions || $[54] !== t10) {
    t11 = <input ref={inputRef} id={inputId} type="text" role="combobox" aria-expanded={showSuggestions} aria-autocomplete="list" value={field.inputValue} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={handleFocus} onBlur={handleBlur} placeholder={t10} autoFocus={autoFocus} autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" data-form-type="other" className="min-w-[60px] flex-1 appearance-none border-0 bg-transparent p-0 text-body text-foreground outline-none placeholder:text-foreground-muted focus:outline-none focus:ring-0" />;
    $[46] = autoFocus;
    $[47] = field.inputValue;
    $[48] = handleBlur;
    $[49] = handleChange;
    $[50] = handleFocus;
    $[51] = handleKeyDown;
    $[52] = inputId;
    $[53] = showSuggestions;
    $[54] = t10;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== t11 || $[57] !== t2 || $[58] !== t3 || $[59] !== t4 || $[60] !== t5) {
    t12 = <div onMouseDown={t2} className={t3}>{t4}{t5}{t11}</div>;
    $[56] = t11;
    $[57] = t2;
    $[58] = t3;
    $[59] = t4;
    $[60] = t5;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  let t13;
  if ($[62] !== T0 || $[63] !== t12 || $[64] !== t6) {
    t13 = <T0 asChild={t6}>{t12}</T0>;
    $[62] = T0;
    $[63] = t12;
    $[64] = t6;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== field.error) {
    t14 = field.error && <p className="text-caption text-status-error-text">{field.error}</p>;
    $[66] = field.error;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  let t15;
  if ($[68] !== t13 || $[69] !== t14 || $[70] !== t7) {
    t15 = <div className={t7}>{t13}{t14}</div>;
    $[68] = t13;
    $[69] = t14;
    $[70] = t7;
    $[71] = t15;
  } else {
    t15 = $[71];
  }
  let t16;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cx("z-[60] max-h-60 w-[var(--radix-popover-trigger-width)] overflow-auto", "rounded-md border border-border bg-background p-1 shadow-lg");
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  let t17;
  if ($[73] !== activeIndex || $[74] !== selectSuggestion || $[75] !== suggestions) {
    let t18;
    if ($[77] !== activeIndex || $[78] !== selectSuggestion) {
      t18 = (suggestion_0, index) => <div key={`${suggestion_0.kind}:${suggestion_0.email}`} onMouseDown={_temp5} onClick={() => selectSuggestion(suggestion_0)} onMouseEnter={() => setHighlightedIndex(index)} className={cx("flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-body text-foreground transition-colors", "hover:bg-hover", index === activeIndex && "bg-hover")}>{suggestion_0.kind === "raw" ? <><span className="min-w-0 truncate">{suggestion_0.email}</span><span className="shrink-0 text-caption text-foreground-muted">Add address</span></> : <><span className="flex min-w-0 items-baseline gap-2">{suggestion_0.name && <span className="truncate">{suggestion_0.name}</span>}<span className="min-w-0 truncate text-foreground-muted">{suggestion_0.email}</span></span>{suggestion_0.kind === "patient" && <span className="shrink-0 text-caption text-foreground-muted">Patient</span>}</>}</div>;
      $[77] = activeIndex;
      $[78] = selectSuggestion;
      $[79] = t18;
    } else {
      t18 = $[79];
    }
    t17 = suggestions.map(t18);
    $[73] = activeIndex;
    $[74] = selectSuggestion;
    $[75] = suggestions;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[80] !== t17) {
    t18 = <PopoverPrimitives.Portal><PopoverPrimitives.Content align="start" sideOffset={4} onOpenAutoFocus={_temp} onCloseAutoFocus={_temp2} onPointerDownOutside={_temp3} onInteractOutside={_temp4} className={t16}>{t17}</PopoverPrimitives.Content></PopoverPrimitives.Portal>;
    $[80] = t17;
    $[81] = t18;
  } else {
    t18 = $[81];
  }
  let t19;
  if ($[82] !== T1 || $[83] !== t15 || $[84] !== t18 || $[85] !== t8 || $[86] !== t9) {
    t19 = <T1 open={t8} onOpenChange={t9}>{t15}{t18}</T1>;
    $[82] = T1;
    $[83] = t15;
    $[84] = t18;
    $[85] = t8;
    $[86] = t9;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  return t19;
}
function _temp5(e_6) {
  return e_6.preventDefault();
}
function _temp4(e_5) {
  return e_5.preventDefault();
}
function _temp3(e_4) {
  return e_4.preventDefault();
}
function _temp2(e_3) {
  return e_3.preventDefault();
}
function _temp(e_2) {
  return e_2.preventDefault();
}